import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, ProgressBar, Row, Col, Card } from 'react-bootstrap';
import { submitBookingRequest, requestAgencyMembership, checkAgencyMembershipStatus, fetchAgencies, uploadDocument } from '../services/api';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './BookingRequestForm.css';

const BookingRequestForm = ({ lot, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
  });
  
  // Multiple document types
  const [chequeFile, setChequeFile] = useState(null);
  const [icFrontFile, setIcFrontFile] = useState(null);
  const [icBackFile, setIcBackFile] = useState(null);
  
  // Preview images
  const [chequePreview, setChequePreview] = useState(null);
  const [icFrontPreview, setIcFrontPreview] = useState(null);
  const [icBackPreview, setIcBackPreview] = useState(null);
  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLogged, setIsLogged] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const { currentUser, isAgencyMember, hasPendingAgencyRequest, canRequestBookings, refreshUser } = useAuth();

  // State for agency membership request form
  const [agencyData, setAgencyData] = useState({
    agencyName: '',
    agencyId: '',
    position: '',
    message: ''
  });
  const [agencyFormErrors, setAgencyFormErrors] = useState({});
  const [agencies, setAgencies] = useState([]);
  const [showAgencyForm, setShowAgencyForm] = useState(false);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  
  // Load available agencies
  const loadAgencies = async () => {
    // Always set default agencies first to ensure dropdown isn't empty
    const defaultAgencies = [
      { _id: "1", name: "AeroIndustrial Property Group" },
      { _id: "2", name: "Global Industrial Park Management" },
      { _id: "3", name: "Heritage Properties Inc." },
      { _id: "4", name: "Malaysia Industrial Network" },
      { _id: "5", name: "Commercial Estates Agency" }
    ];
    
    // Set the defaults immediately so the dropdown is never empty
    setAgencies(defaultAgencies);
    
    try {
      setLoadingAgencies(true);
      console.log('Loading agencies for membership request form...');
      
      // Make API call to fetch agencies
      const agencyList = await fetchAgencies();
      console.log('Agencies loaded:', agencyList);
      
      if (agencyList && Array.isArray(agencyList) && agencyList.length > 0) {
        // Only update if we got real data from the API
        setAgencies(agencyList);
      } else {
        console.error('No agencies returned or empty array, keeping default list');
      }
    } catch (err) {
      console.error('Error loading agencies:', err);
      // Default agencies already set, so no need to reset them
    } finally {
      setLoadingAgencies(false);
    }
  };

  // Combined useEffect for all initialization logic
  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    setIsLogged(!!token);
    
    // Check agency status from server then update UI
    const checkServerAgencyStatus = async () => {
      try {
        if (token) {
          const userData = await checkAgencyMembershipStatus();
          // Refresh the auth context
          if (refreshUser) refreshUser();
          
          // Calculate show states based on latest data
          const hasAgency = userData.agencyId && userData.agencyStatus === 'approved';
          const hasPending = userData.agencyId && userData.agencyStatus === 'pending';
          
          setShowAgencyForm(!hasAgency && !hasPending);
        }
      } catch (err) {
        console.error('Error checking agency status:', err);
      }
    };
    
    checkServerAgencyStatus();
    
    // Check if user needs to request agency membership first
    if (currentUser && !isAgencyMember() && !hasPendingAgencyRequest()) {
      setShowAgencyForm(true);
    } else {
      setShowAgencyForm(false);
    }

    // Load agencies regardless of showAgencyForm status
    loadAgencies();
  }, [currentUser, isAgencyMember, hasPendingAgencyRequest, refreshUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  // Handle changes in agency membership form
  const handleAgencyChange = (e) => {
    const { name, value } = e.target;
    
    // If selecting an agency from dropdown, update both agencyId and agencyName
    if (name === 'agencyId' && value) {
      const selectedAgency = agencies.find(agency => agency._id === value);
      if (selectedAgency) {
        setAgencyData(prev => ({
          ...prev,
          agencyId: value,
          agencyName: selectedAgency.name
        }));
      } else {
        setAgencyData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setAgencyData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear errors when user types
    if (agencyFormErrors[name]) {
      setAgencyFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Handle agency membership request submission
  const handleAgencySubmit = async (e) => {
    e.preventDefault();
    
    // Validate agency form
    const errors = {};
    if (!agencyData.agencyId) errors.agencyId = 'Please select an agency';
    if (!agencyData.position) errors.position = 'Your position is required';
    
    if (Object.keys(errors).length > 0) {
      setAgencyFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Make API call to request agency membership using the imported function
      const response = await requestAgencyMembership({
        agencyId: agencyData.agencyId,
        agencyName: agencyData.agencyName,
        position: agencyData.position,
        message: agencyData.message || 'Requesting agency membership'
      });
      
      setSuccess('Agency membership request submitted successfully! Please wait for approval before booking.');
      // Refresh the page or user context to reflect pending status
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      console.error('Error submitting agency membership request:', err);
      setError(err.response?.data?.message || 'Failed to submit agency membership request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle document file selection by type
  const handleFileChange = (e, setFileFunction, previewSetter) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setFileFunction(file);
      
      // Generate preview for images
      if (previewSetter && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          previewSetter(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    const isAgentAdmin = currentUser?.role === 'agent_admin';
    const hasAgencyMembership = isAgencyMember();
    
    // Common validations for all users
    if (!formData.name) errors.name = isAgentAdmin ? 'Agent name is required' : 'Full name is required';
    if (!formData.email) errors.email = 'Email is required';
    if (!formData.phone) errors.phone = 'Phone number is required';
    
    // Email validation
    if (formData.email && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Document validation for agent admins (required) and agency members (optional)
    if (isAgentAdmin) {
      if (!chequeFile) errors.cheque = 'Payment cheque document is required';
      if (!icFrontFile) errors.icFront = 'IC front image is required';
      if (!icBackFile) errors.icBack = 'IC back image is required';
    }
    // For agency members, documents are optional but if one IC is uploaded, both should be uploaded
    else if (hasAgencyMembership) {
      if ((icFrontFile && !icBackFile) || (!icFrontFile && icBackFile)) {
        errors.icFront = 'Both IC front and back are required if uploading IC';
        errors.icBack = 'Both IC front and back are required if uploading IC';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user can request bookings (has active agency membership)
    if (!canRequestBookings()) {
      setError('You must be an approved agency member to request bookings');
      setShowAgencyForm(true);
      return;
    }
    
    // Reset any existing errors
    setError('');
    setFormErrors({});
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const isAgentAdmin = currentUser?.role === 'agent_admin';
      const hasAgencyMembership = isAgencyMember();
      
      // Prepare booking request data (without document URLs initially)
      const requestData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        description: formData.description,
        lot: lot, // Pass the complete lot object
        lotId: lot._id || lot.id || lot.lotNumber, // Use _id, id, or lotNumber as fallback
        // Explicitly set status based on user role
        status: currentUser?.role === 'agent_admin' ? 'pending_admin' : 'pending_agent'
      };
      
      // Debug log to help troubleshoot
      console.log('Booking request data:', {
        lotId: requestData.lotId,
        lotObject: lot,
        hasLotId: !!requestData.lotId
      });
      
      // Submit booking request first
      const response = await submitBookingRequest(requestData);
      
      // Get the booking ID from the response
      const bookingId = response.data?._id || response.data?.id;
      
      if (bookingId && (isAgentAdmin || hasAgencyMembership)) {
        // Upload documents after booking is created
        try {
          if (chequeFile) {
            console.log('Uploading cheque document...');
            await uploadDocument(chequeFile, 'cheque', bookingId);
          }
          if (icFrontFile) {
            console.log('Uploading IC front document...');
            await uploadDocument(icFrontFile, 'ic_front', bookingId);
          }
          if (icBackFile) {
            console.log('Uploading IC back document...');
            await uploadDocument(icBackFile, 'ic_back', bookingId);
          }
          console.log('All documents uploaded successfully');
        } catch (uploadError) {
          console.error('Error uploading documents:', uploadError);
          // Don't fail the entire booking if document upload fails
          setError('Booking created successfully, but there was an issue uploading documents. You can upload them later.');
        }
      }
      
      setSuccess('Booking request submitted successfully!');
      if (onSuccess) {
        onSuccess(response);
      }
      
      // Clear form
      setFormData({
        name: '',
        email: '',
        phone: '',
        description: ''
      });
      
      // Clear files if user can upload documents
      if (isAgentAdmin || hasAgencyMembership) {
        setChequeFile(null);
        setIcFrontFile(null);
        setIcBackFile(null);
        setIcFrontPreview(null);
        setIcBackPreview(null);
      }
      
    } catch (error) {
      console.error('Error submitting booking request:', error);
      setError(error.response?.data?.message || 'Failed to submit booking request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel button click
  const handleCancel = (e) => {
    if (e) {
      e.preventDefault(); // Prevent default button behavior
      e.stopPropagation(); // Stop event propagation
    }
    
    // Clear form data before unmounting
    setFormData({
      name: '',
      email: '',
      phone: '',
      description: '',
    });
    
    // Clear agency data
    setAgencyData({
      agencyName: '',
      agencyId: '',
      position: '',
      message: ''
    });
    
    // Clear files
    setChequeFile(null);
    setIcFrontFile(null);
    setIcBackFile(null);
    setIcFrontPreview(null);
    setIcBackPreview(null);
    
    // Clear errors and success messages
    setError('');
    setSuccess('');
    setFormErrors({});
    setAgencyFormErrors({});
    
    // Call onCancel after state cleanup
    if (typeof onCancel === 'function') {
      setTimeout(() => {
        onCancel();
      }, 0);
    }
  };

  // If the user needs to request agency membership first, show that form
  if (showAgencyForm) {
    return (
      <div className="booking-request-form">
        <h3>Agency Membership Required</h3>
        <Alert variant="info">
          To book this property, you must first be an approved member of an agency. 
          Please submit an agency membership request below.
        </Alert>
        
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Card>
          <Card.Header className="form-card-header">Request Agency Membership</Card.Header>
          <Card.Body>
            <Form onSubmit={handleAgencySubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Select Agency <span className="required-mark">*</span></Form.Label>
                {loadingAgencies ? (
                  <div>
                    <div>Loading agencies...</div>
                    <Form.Select
                      name="agencyId"
                      value={agencyData.agencyId}
                      onChange={handleAgencyChange}
                      isInvalid={!!agencyFormErrors.agencyId}
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">-- Select an Agency --</option>
                      {agencies.map(agency => (
                        <option key={agency._id} value={agency._id}>
                          {agency.name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                ) : (
                  <>
                    <Form.Select
                      name="agencyId"
                      value={agencyData.agencyId}
                      onChange={handleAgencyChange}
                      isInvalid={!!agencyFormErrors.agencyId}
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">-- Select an Agency --</option>
                      {agencies.map(agency => (
                        <option key={agency._id} value={agency._id}>
                          {agency.name}
                        </option>
                      ))}
                    </Form.Select>
                    {agencies.length === 0 && (
                      <div className="text-danger mt-1">
                        No agencies loaded. Please reload the page or contact support.
                      </div>
                    )}
                    <div className="small text-muted">
                      {agencies.length > 0 ? `${agencies.length} agencies available` : 'No agencies loaded'}
                    </div>
                  </>
                )}
                <Form.Control.Feedback type="invalid">
                  {agencyFormErrors.agencyId}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Your Position <span className="required-mark">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="position"
                  value={agencyData.position}
                  onChange={handleAgencyChange}
                  isInvalid={!!agencyFormErrors.position}
                  disabled={isSubmitting}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {agencyFormErrors.position}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Additional Information</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="message"
                  value={agencyData.message}
                  onChange={handleAgencyChange}
                  disabled={isSubmitting}
                />
              </Form.Group>
              
              <div className="d-flex justify-content-between">
                <Button variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Membership Request'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    );
  }
  
  // If user has a pending agency request, show a message
  if (hasPendingAgencyRequest()) {
    return (
      <div className="booking-request-form">
        <h3>Agency Membership Pending</h3>
        <Alert variant="warning">
          Your agency membership request is currently pending approval. 
          You'll be able to book properties once your membership is approved.
        </Alert>
        <Button variant="secondary" onClick={handleCancel}>
          Back
        </Button>
      </div>
    );
  }

  // Show different forms based on user role
  const isAgentAdmin = currentUser?.role === 'agent_admin';

  return (
    <div className="booking-request-form">
      <h3>Request Booking for {lot.lotNumber || 'Selected Lot'}</h3>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Form onSubmit={handleSubmit} noValidate>
        <Card className="mb-4">
          <Card.Header className="form-card-header">
            {isAgentAdmin ? 'Agent Information' : 'Booking Request Information'}
          </Card.Header>
          <Card.Body>
            <Form.Group controlId="name" className="mb-3">
              <Form.Label>{isAgentAdmin ? 'Agent Name' : 'Full Name'} <span className="required-mark">*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={isAgentAdmin ? "Enter agent name" : "Enter your full name"}
                disabled={isSubmitting}
                required
                isInvalid={!!formErrors.name}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group controlId="email" className="mb-3">
                  <Form.Label>Email <span className="required-mark">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    disabled={isSubmitting}
                    required
                    isInvalid={!!formErrors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="phone" className="mb-3">
                  <Form.Label>Phone <span className="required-mark">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    disabled={isSubmitting}
                    required
                    isInvalid={!!formErrors.phone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.phone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group controlId="description" className="mb-3">
              <Form.Label>{isAgentAdmin ? 'Description' : 'Request Message'}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={isAgentAdmin ? "Additional notes or details for this booking request" : "Please provide any additional information about your booking request"}
                disabled={isSubmitting}
              />
            </Form.Group>
          </Card.Body>
        </Card>
        
{(isAgentAdmin || isAgencyMember()) && (
          <Card className="mb-4">
            <Card.Header className="form-card-header">
              Document Uploads {isAgentAdmin ? '(Required)' : '(Optional)'}
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>
                  Payment Cheque {isAgentAdmin && <span className="required-mark">*</span>}
                </Form.Label>
                <Form.Control 
                  type="file"
                  onChange={(e) => handleFileChange(e, setChequeFile)}
                  disabled={isSubmitting}
                  isInvalid={!!formErrors.cheque}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.cheque}
                </Form.Control.Feedback>
                {!isAgentAdmin && (
                  <Form.Text className="text-muted">
                    Optional: Upload payment cheque if available
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  IC Front {isAgentAdmin && <span className="required-mark">*</span>}
                </Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e) => handleFileChange(e, setIcFrontFile, setIcFrontPreview)}
                  disabled={isSubmitting}
                  isInvalid={!!formErrors.icFront}
                  accept="image/*"
                />
                {icFrontPreview && (
                  <img src={icFrontPreview} alt="IC Front Preview" className="mt-2 preview-image" />
                )}
                <Form.Control.Feedback type="invalid">
                  {formErrors.icFront}
                </Form.Control.Feedback>
                {!isAgentAdmin && (
                  <Form.Text className="text-muted">
                    Optional: Upload front side of IC
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  IC Back {isAgentAdmin && <span className="required-mark">*</span>}
                </Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e) => handleFileChange(e, setIcBackFile, setIcBackPreview)}
                  disabled={isSubmitting}
                  isInvalid={!!formErrors.icBack}
                  accept="image/*"
                />
                {icBackPreview && (
                  <img src={icBackPreview} alt="IC Back Preview" className="mt-2 preview-image" />
                )}
                <Form.Control.Feedback type="invalid">
                  {formErrors.icBack}
                </Form.Control.Feedback>
                {!isAgentAdmin && (
                  <Form.Text className="text-muted">
                    Optional: Upload back side of IC
                  </Form.Text>
                )}
              </Form.Group>
            </Card.Body>
          </Card>
        )}
        
        <div className="d-flex justify-content-between">
          <Button variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default BookingRequestForm; 